import React, {useMemo} from 'react';
import clsx from 'clsx';
import {i18n} from '@netx/core/i18n';
import {enumToValues} from '@netx/core-next/utils/enum';
import {SelectField} from '@netx/core-components/SelectField';
import {TextField} from '@netx/core-components/TextField';
import {composeClasses, styled, WithClasses} from '@netx/core-components/Theme';
import {SearchSubTypeLabel} from '../../../../locales';
import {CommonRuleEditorProps} from '../../../types';
import {KeywordType} from '../../constants';
import {getKeywordTypeLabel} from '../../utils';
import {
	keywordRuleEditorClasses,
	getKeywordRuleEditorUtilityClass,
	KeywordRuleEditorClasses,
	KEYWORD_RULE_EDITOR_CLASS_NAME,
} from './keywordRuleEditorClasses';

export type KeywordRuleEditorOwnerState = WithClasses<
	KeywordRuleEditorClasses,
	{}
>;

const useUtilityClasses = (ownerState: KeywordRuleEditorOwnerState) => {
	const {classes} = ownerState;
	const slots = {
		root: ['root'],
	};
	return composeClasses(slots, getKeywordRuleEditorUtilityClass, classes);
};

const KeywordRuleEditorRoot = styled('div', {
	name: KEYWORD_RULE_EDITOR_CLASS_NAME,
	slot: 'Root',
})(({theme}) => ({
	display: 'flex',
	flex: '1 1 auto',
	'& > * + *': {
		marginLeft: `${theme.spacing(1)}`,
	},
}));

export type KeywordRuleEditorProps = WithClasses<
	KeywordRuleEditorClasses,
	Pick<CommonRuleEditorProps, 'validationErrors'> & {
		elementSubType1: KeywordType;
		elementSubType1Options?: KeywordType[];
		elementValue1: string;
		onSubType1Change: (subType1: KeywordType) => void;
		onValue1Change: (value: string) => void;
	}
>;

const KeywordRuleEditorInner = ({
	classes: _classes,
	className,
	elementSubType1,
	elementSubType1Options,
	elementValue1,
	validationErrors,
	onSubType1Change,
	onValue1Change,
}: KeywordRuleEditorProps) => {
	const ownerState: KeywordRuleEditorOwnerState = {
		classes: _classes,
		className,
	};

	const classes = useUtilityClasses(ownerState);

	const _elementSubType1Options = useMemo(
		() => elementSubType1Options || enumToValues(KeywordType),
		[elementSubType1Options],
	);

	return (
		<KeywordRuleEditorRoot className={clsx(classes.root, className)}>
			<SelectField<KeywordType, false, true, false>
				data-testid="keyword-rule-sub-type-1"
				disableClearable
				label={SearchSubTypeLabel.MATCH_TYPE}
				multiple={false}
				name={'rule-sub-type-1'}
				options={_elementSubType1Options}
				value={elementSubType1}
				getOptionLabel={getKeywordTypeLabel}
				onChange={(event, value) => onSubType1Change(value)}
			/>

			<TextField
				data-testid="keyword-rule-value-1"
				error={validationErrors?.elementValue1?.message}
				fullWidth
				label={i18n.t('i18n.keywords')}
				name={'rule-value-1'}
				value={elementValue1.toString()}
				onChange={(event, value) => onValue1Change(value)}
			/>
		</KeywordRuleEditorRoot>
	);
};

export const KeywordRuleEditorPrimitive = KeywordRuleEditorInner;

export const KeywordRuleEditor = KeywordRuleEditorPrimitive;

export default KeywordRuleEditor;

export type KeywordRuleEditorGenericProps = WithClasses<
	KeywordRuleEditorClasses,
	CommonRuleEditorProps
>;

export const KeywordRuleEditorGenericPrimitive = ({
	elementValue1,
	...props
}: KeywordRuleEditorGenericProps) => {
	return (
		<KeywordRuleEditor {...props} elementValue1={elementValue1.toString()} />
	);
};

export const KeywordRuleEditorGeneric = KeywordRuleEditorGenericPrimitive;
